import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../../components/AuthLayout.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.tsx';
import { Input } from '../../../components/ui/input.tsx';
import useParent from '../../../hooks/useParent.js';
import { LoadingSpinner } from '../../../components/ui/loading-spinner.tsx';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string().min(6, {
    message: 'Password must be at least 6 characters.',
  }),
});

const ParentDashboardSignIn = () => {
  // const [isPassword, setIsPassword] = useState(false);
  const { parentLogin, parentLoginLoading } = useParent();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values) => {
    try {
      await parentLogin(values);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.message || 'Failed to Login. Please try again.');
      form.setError('root', {
        type: 'manual',
        message: error.message || 'Failed to Login. Please try again.',
      });
    }
  };

  return (
    <AuthLayout
      title="  Hey, Hello"
      subtitle="Enter your login credentials to access your account"
      wave
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {form.formState.errors.root && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
              {form.formState.errors.root.message}
            </div>
          )}

          <p className="font-normal text-2xl">Login</p>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal text-sm text-[#2f2f2f]">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="example@mail.com"
                    {...field}
                    disabled={parentLoginLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal text-sm text-[#2f2f2f]">
                  Password
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                      disabled={parentLoginLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      disabled={parentLoginLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={parentLoginLoading}
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-900"
              >
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate('/parent/forgot-password')}
                className="font-medium text-purple-600 hover:text-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={parentLoginLoading}
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={parentLoginLoading}
              className="w-full h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {parentLoginLoading ? (
                <div className="flex items-center justify-center w-full">
                  <LoadingSpinner size="sm" className="mr-2" />
                  <span>Signing in...</span>
                </div>
              ) : (
                'Login'
              )}
            </button>
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
};

export default ParentDashboardSignIn;
